import { useCallback } from "react";

import {
  ReportGoalInput,
  ReportTargetInput,
  useCreateUpdateCustomFieldValueMutation,
  useCreateUpdateReportGoalMutation,
  useCreateUpdateReportTargetMutation,
} from "@api/graphql/types-and-hooks";

export interface SaveFieldsResult {
  success: boolean;
  error?: string;
  /** Global ID of the custom field row created/updated by this save, when applicable. */
  customFieldId?: string;
}

interface UseInlineTreatmentPlanEditProps {
  goalId?: string;
  reportId: string;
  domainId: string | null;
  learnerId: string;
  targetId?: string;
  onSuccess?: () => void;
}

/**
 * Handles native goal fields, native target fields,
 * and custom fields.
 */
export function useInlineTreatmentPlanEdit({
  goalId,
  reportId,
  domainId,
  learnerId,
  targetId = "",
  onSuccess,
}: UseInlineTreatmentPlanEditProps) {
  const goalMutation = useCreateUpdateReportGoalMutation({});
  const targetMutation = useCreateUpdateReportTargetMutation({});
  const customFieldMutation = useCreateUpdateCustomFieldValueMutation({});

  const saveGoalFields = useCallback(
    async (fields: Partial<ReportGoalInput>): Promise<SaveFieldsResult> => {
      try {
        const sanitizedFields = Object.fromEntries(
          Object.entries(fields).map(([key, value]) => [key, value === "" ? null : value]),
        ) as Partial<ReportGoalInput>;

        const response = await goalMutation.mutateAsync({
          goalInput: {
            ...sanitizedFields,
            reportGoalId: goalId,
            reportId,
            reportDomainId: domainId,
          },
          learnerId: learnerId || "<missing-learner-id>",
        });

        if (response.createUpdateReportGoal?.status) {
          onSuccess?.();
          return { success: true };
        }
        return { success: false, error: "Failed to save changes" };
      } catch (error) {
        console.error("Error saving goal fields:", error);
        return { success: false, error: "Failed to save changes" };
      }
    },
    [goalMutation, goalId, reportId, domainId, learnerId, onSuccess],
  );

  const saveTargetFields = useCallback(
    async (fields: Partial<ReportTargetInput>): Promise<SaveFieldsResult> => {
      if (!goalId || goalId.trim() === "") {
        return { success: false, error: "Goal ID is required to save target" };
      }

      try {
        const sanitizedFields = Object.fromEntries(
          Object.entries(fields).map(([key, value]) => [key, value === "" ? null : value]),
        ) as Partial<ReportTargetInput>;

        const response = await targetMutation.mutateAsync({
          targetData: {
            targetId: targetId.length > 0 ? targetId : undefined,
            reportGoalId: goalId,
            ...sanitizedFields,
            reportId,
          },
          learnerId: learnerId || "<missing-learner-id>",
        });

        if (response.createUpdateReportTarget?.status) {
          onSuccess?.();
          return { success: true };
        }
        return { success: false, error: "Failed to save changes" };
      } catch (error) {
        console.error("Error saving target fields:", error);
        return { success: false, error: "Failed to save changes" };
      }
    },
    [targetMutation, goalId, targetId, reportId, learnerId, onSuccess],
  );

  /**
   * Issue the createUpdateCustomFieldValue mutation. When `customFieldId` is
   * null, the backend upserts by (entityId, fieldIdentifier) and creates the
   * row from the template spec if needed.
   */
  const runCustomFieldMutation = useCallback(
    async (variables: {
      customFieldId?: string;
      content: string;
      fieldIdentifier?: string;
      reportGoalId?: string;
      reportTargetId?: string;
      reportDomainId?: string;
    }): Promise<SaveFieldsResult> => {
      try {
        const response = await customFieldMutation.mutateAsync(variables);
        if (response.createUpdateCustomFieldValue?.status) {
          onSuccess?.();
          return { success: true, customFieldId: response.createUpdateCustomFieldValue?.customField?.id };
        }
        return {
          success: false,
          error: response.createUpdateCustomFieldValue?.message || "Failed to save custom field",
        };
      } catch (error) {
        console.error("Error saving custom field:", error);
        return { success: false, error: "Failed to save custom field" };
      }
    },
    [customFieldMutation, onSuccess],
  );

  const saveGoalCustomField = useCallback(
    async (fieldIdentifier: string, content: string, customFieldId: string | null): Promise<SaveFieldsResult> => {
      if (customFieldId) {
        return runCustomFieldMutation({ customFieldId, content });
      }
      if (!goalId) return { success: false, error: "Missing goal id" };
      return runCustomFieldMutation({
        content,
        fieldIdentifier,
        reportGoalId: goalId,
      });
    },
    [runCustomFieldMutation, goalId],
  );

  const saveTargetCustomField = useCallback(
    async (fieldIdentifier: string, content: string, customFieldId: string | null): Promise<SaveFieldsResult> => {
      if (customFieldId) {
        return runCustomFieldMutation({ customFieldId, content });
      }
      if (!targetId) return { success: false, error: "Missing target id" };
      return runCustomFieldMutation({
        content,
        fieldIdentifier,
        reportTargetId: targetId,
      });
    },
    [runCustomFieldMutation, targetId],
  );

  const saveDomainCustomField = useCallback(
    async (fieldIdentifier: string, content: string, customFieldId: string | null): Promise<SaveFieldsResult> => {
      if (customFieldId) {
        return runCustomFieldMutation({ customFieldId, content });
      }
      if (!domainId) return { success: false, error: "Missing domain id" };
      return runCustomFieldMutation({
        content,
        fieldIdentifier,
        reportDomainId: domainId,
      });
    },
    [runCustomFieldMutation, domainId],
  );

  return {
    saveGoalFields,
    saveTargetFields,
    saveGoalCustomField,
    saveTargetCustomField,
    saveDomainCustomField,
    isSaving: goalMutation.isPending || targetMutation.isPending || customFieldMutation.isPending,
  };
}
